/**
 =========================================================
 * Soft UI Dashboard PRO React - v3.0.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
 * Copyright 2021 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import '@upheal/shared/utils/polyfills';
import '@fontsource-variable/geist';
import '@fontsource/bad-script';

import { lazy, PropsWithChildren, Suspense } from 'react';

import { initSentry } from '@upheal/shared/utils/sentry-setup';
import { initSnowplowTracker } from '@upheal/shared/utils/analytics/snowplow';

import '@upheal/design-system/fonts/material-icons.css';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { DialogsProvider } from '@toolpad/core';
import theme from '@upheal/design-system/theme';
import { SnackbarProvider } from '@upheal/design-system/vendor/notistack';
import { enableMapSet } from 'immer';
import { ApolloProvider } from '@apollo/client';
import { apolloClient } from '@upheal/shared/utils/apollo';
import { browserName } from 'react-device-detect';
import BrowserUnsupported from './components/Errors/BrowserUnsupported';
import '@upheal/shared/utils/page-load-utils';
import { ConsentConfirmationProvider } from '@upheal/shared/components/shared/Consent/useConsentConfirmation';
import { MultipartUploadManagerProvider } from '@upheal/shared/utils/media/MultipartUploadManagerProvider';
import { ReactQueryProvider } from '@upheal/shared/providers/ReactQueryProvider';
import { createRoot } from 'react-dom/client';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { BrowserRouter, Route } from 'react-router-dom';
import BlockApp from './components/misc/BlockApp';
import { UpgradeDialogProvider } from '@upheal/shared/components/shared/Billing/PremiumFeature/UpgradeDialogProvider';
import { PostHogProvider } from 'posthog-js/react';
import { PostHogConfig } from 'posthog-js';
import { DATA_UI_TRACKING } from '@upheal/shared/utils/analytics/ui-tracking';
import { isE2ERunning } from '@upheal/shared/utils/configuration';

const isTrainingDemoRoute = window.location.pathname.startsWith('/training-demo');
const AppStateProvider = lazy(() => import('@upheal/shared/state'));
const CodeSplittingRouting = lazy(() => import('./CodeSplittingRouting'));
const TrainingDemoApp = lazy(() =>
    import('./components/TrainingDemo/TrainingDemoApp').then((module) => ({
        default: module.TrainingDemoApp,
    })),
);
const postHogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const postHogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
const isPostHogConfigured =
    Boolean(postHogKey) &&
    Boolean(postHogHost) &&
    !postHogHost.includes('example.com');

initSentry();
if (!isTrainingDemoRoute) {
    initSnowplowTracker();
}
enableMapSet();

const options: Partial<PostHogConfig> = {
    api_host: postHogHost,
    // To be absolutely sure no PII is sent to PostHog
    mask_all_text: true,
    session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
    },
    autocapture: {
        // Only elements with uiEventDataAttrs spread are autocaptured
        css_selector_allowlist: [`[${DATA_UI_TRACKING}="true"]`],
    },
    // 3 seconds timeout for feature flags initial load
    // this is the default, just made explicit
    feature_flag_request_timeout_ms: 3000,
    // Disable surveys when running E2E tests to prevent them from overlapping UI elements
    disable_surveys: isE2ERunning(),
    loaded: (posthog) => {
        // Set pathname for feature flags as soon as PostHog is loaded
        // regardless user identification so that it's available also for anonymous users
        posthog.setPersonPropertiesForFlags({
            $pathname: window.location.pathname,
        });
    },
};

const InternalPostHogProvider = ({ children }: PropsWithChildren) => {
    if (isTrainingDemoRoute || !isPostHogConfigured) {
        return <>{children}</>;
    }

    return (
        <PostHogProvider
            apiKey={postHogKey}
            options={options}
        >
            {children}
        </PostHogProvider>
    );
};

const CommonProviders = ({ children }: PropsWithChildren) => {
    return (
        <InternalPostHogProvider>
            <ReactQueryProvider>
                <ApolloProvider client={apolloClient}>
                    <ThemeProvider theme={theme}>
                        <MultipartUploadManagerProvider>
                            <style>
                                {`
                          body > iframe { display: none }
                          `}
                            </style>
                            <CssBaseline />
                            <SnackbarProvider>
                                <DialogsProvider>
                                    <ConsentConfirmationProvider>
                                        <UpgradeDialogProvider>
                                            {children}
                                        </UpgradeDialogProvider>
                                    </ConsentConfirmationProvider>
                                </DialogsProvider>
                            </SnackbarProvider>
                        </MultipartUploadManagerProvider>
                    </ThemeProvider>
                </ApolloProvider>
            </ReactQueryProvider>
        </InternalPostHogProvider>
    );
};

// NOTE set to true to block access to the app
const blockAccess =
    import.meta.env.VITE_REACT_APP_UPHEAL_BLOCK_ACCESS === 'true';
// When the block ends '2023-08-29T10:00:00.000Z'. If not specified, it will be 1 hour from now
const blockAccessFinishTime = `${
    import.meta.env.VITE_REACT_APP_UPHEAL_BLOCK_ACCESS_FINISH_TIME
}`;

const domNode = document.getElementById('root')!;
const root = createRoot(domNode);

const TrainingDemoRoot = () => (
    <AppStateProvider>
        <CommonProviders>
            <Suspense fallback={null}>
                <Route path="/training-demo" component={TrainingDemoApp} />
            </Suspense>
        </CommonProviders>
    </AppStateProvider>
);

const AppRoot = () => {
    if (isTrainingDemoRoute) {
        return <TrainingDemoRoot />;
    }

    return (
        <Suspense fallback={null}>
            {blockAccess && (
                <CommonProviders>
                    <BlockApp finishTimestamp={blockAccessFinishTime} />
                </CommonProviders>
            )}
            {!blockAccess &&
                (browserName === 'DuckDuckGo' ? (
                    <CommonProviders>
                        <BrowserUnsupported />
                    </CommonProviders>
                ) : (
                    <AppStateProvider>
                        <CommonProviders>
                            <CodeSplittingRouting />
                        </CommonProviders>
                    </AppStateProvider>
                ))}
        </Suspense>
    );
};

root.render(
    <BrowserRouter>
        <CompatRouter>
            <AppRoot />
        </CompatRouter>
    </BrowserRouter>,
);
